import { standardInflectors } from '@orbit/serializers'

import PlainDate from '../lib/PlainDate'
import { dateRangeToTimeRange } from '../lib/time'

const { dasherize } = standardInflectors

export type FilterValue = [PlainDate, PlainDate] | string

function transformFilterValue(value: FilterValue, { timezone }): string | undefined {
  if(typeof(value) === 'object' && value instanceof Array && value.length === 2 && value.every(v => typeof(v) === 'object' && v instanceof PlainDate)) {
    return dateRangeToTimeRange(value, timezone).timeRange?.map(m => m.format()).toString()
  } else {
    return value.toString()
  }
}

function dasherizePrimaryField(field) {
  if (field.includes(':')) {
    const [prefix, rest] = field.split(':', 2);
    return `${dasherize(prefix)}:${rest}`;
  } else {
    return dasherize(field)
  }
}

export type BuildQueryParamsProps = {
  sortColumn
  sortOrder
  page?: number
  pageSize?: number
  filters?: { field: string, value: FilterValue }[]
  timezone: string
}

export type QueryParam = [string, string | number]

export function buildQueryParams({ sortColumn, sortOrder, page=1, pageSize=10, filters=[], timezone }: BuildQueryParamsProps): QueryParam[] {
  const params: QueryParam[] = [
    ['page', page],
    ['per_page', pageSize],
    ['timezone', timezone]
  ];
  const filterParams = filters
    .map(filter => [`filter[${dasherizePrimaryField(filter.field)}]`, transformFilterValue(filter.value, {timezone})])
    .filter((_key, value) => value !== undefined)

  params.push(...filterParams as QueryParam[]) // TODO: should be able to avoid explicit cast because of the filter() above, but TypeScript is not inferring that the value is non-undefined
  if(sortColumn) {
    params.push([`order[${dasherizePrimaryField(sortColumn)}]`, sortOrder])
  }
  return params
}

export function buildQueryString(query) {
  return buildQueryParams(query)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`).join('&');
}

export function loadTableData(remote, queryFn, addNotification, timezone) {
  return (dataOptions) => {
    const options = {
      sources: {
        remote: {
          settings: {
            params: Object.fromEntries(buildQueryParams({ ...dataOptions, timezone })),
          },
        },
      },
      fullResponse: true,
    }

    return remote.query(queryFn, options)
      .then(response => ({
        data: response.data.map(record => ({ id: record.id, ...record.attributes })),
        page: dataOptions.page,
        totalCount: response.details[0].document.meta.total,
      }))
      .catch(_error => {
        addNotification({variant: 'alert', message: 'Failed to load data.'});
      })
  };
}
